import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { CapabilityGuard } from './components/auth/CapabilityGuard';
import { AccountCapabilityGuard } from './components/auth/AccountCapabilityGuard';
import { ProtectedRoute } from './permissions/ProtectedRoute';
import { WorkspaceAuthorityBoundary, LegacyWorkspaceSurfaceRedirect } from './contexts/WorkspaceAuthorityContext';
import {
  canViewOrganisationCommercial,
  canInviteUsers,
  canManageSystemRoles,
  canManageUsers,
  canViewCompanies,
} from './lib/auth/capabilities';
import { Loader } from './components/ui/Loader';
import BrandLogo from './components/BrandLogo';
import ErrorBoundary from './components/ErrorBoundary';
import LazyLoader from './components/LazyLoader';
import { lazyWithRecovery as lazy } from './lib/lazy/lazyWithDevReload';
import {
  ENABLE_E2E_ROUTES,
  ENABLE_INTERNAL_ROUTES,
  ENVIRONMENT_CONFIGURATION_ERROR,
  IS_DEMO_MODE,
} from './config/env';
import { validateEnvironment } from './lib/runtime/envValidation';
import { resolveIdentityRoles } from './lib/auth/identityRoles';
import {
  InvestorCapabilityGuard,
  InvestorLandingRedirect,
} from './components/auth/InvestorCapabilityGuard';

// Layout and core components
import Layout from './components/Layout/Layout';
import StandaloneSurface from './components/Layout/StandaloneSurface';
import NavigationSyncManager from './components/Navigation/NavigationSyncManager';
import HomeRedirect from './components/Navigation/HomeRedirect';
import AuthWrapper from './features/auth/components/AuthWrapper';

// Lazily loaded pages for better performance
const DashboardSelector = lazy(
  () => import('./features/dashboard/components/DashboardSelector'),
  'features/dashboard/components/DashboardSelector',
);
const InvestorAppShell = lazy(
  () => import('./components/Layout/InvestorAppShell'),
  'components/Layout/InvestorAppShell',
);
const WorkspacePageLayout = lazy(
  () => import('./workspaces/WorkspacePageLayout'),
  'workspaces/WorkspacePageLayout',
);
const TestPage = lazy(() => import('./pages/TestPage'));
const ToastTest = lazy(() => import('./pages/ToastTest'));
const DesignTokensDemo = lazy(() => import('./components/DesignTokensDemo'));
const DebugProgressTest = lazy(() => import('./components/DebugProgressTest'));
const ProgressBarTest = lazy(() => import('./test/ProgressBarTest'));
const WorkspaceProjectsPage = lazy(() => import('./pages/workspace/WorkspaceProjectsPage'));
const WorkspaceSelectionPage = lazy(() => import('./pages/workspace/WorkspaceSelectionPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const WorkspaceProjectDetailPage = lazy(() => import('./pages/workspace/WorkspaceProjectDetailPage'));
const WorkspaceTasksPage = lazy(() => import('./pages/workspace/WorkspaceTasksPage'));
const WorkspaceTaskDetailPage = lazy(() => import('./pages/workspace/WorkspaceTaskDetailPage'));
const WorkspaceReportsPage = lazy(() => import('./pages/workspace/WorkspaceReportsPage').then(module => ({ default: module.WorkspaceReportsPage })));
const WorkspaceReportDetailPage = lazy(() => import('./pages/workspace/WorkspaceReportsPage').then(module => ({ default: module.WorkspaceReportDetailPage })));
// Thin, UI-free route adapter that renders the existing canonical Assets page
// (src/pages/Documents.tsx). It is NOT a new page and owns no rendering.
const WorkspaceAssetsRouteAdapter = lazy(() => import('./pages/workspace/WorkspaceAssetsRouteAdapter'));
// Thin, UI-free index adapter that renders the existing DashboardSelector
// under /workspaces/:workspaceId. It is NOT a new page and owns no rendering.
const WorkspaceDashboardRouteAdapter = lazy(() => import('./pages/workspace/WorkspaceDashboardRouteAdapter'));
const WorkspaceSecurityRouteAdapter = lazy(() => import('./pages/workspace/WorkspaceSecurityRouteAdapter'));
const WorkspaceDecisionsPage = lazy(() => import('./pages/workspace/WorkspaceDecisionsPage'));

/**
 * Legacy project-detail links must resolve a validated workspace before any
 * project UI renders. The canonical destination is independently authorized by
 * WorkspaceAuthorityBoundary and WorkspaceProjectDetailPage.
 */
const LegacyProjectDetailRedirect: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  if (!projectId) return <Navigate to="/unauthorized" replace />;
  return (
    <LegacyWorkspaceSurfaceRedirect
      subPath={`projects/${encodeURIComponent(projectId)}`}
      connectedProjectId={projectId}
    />
  );
};

/** Keep legacy Gate Journey links in the validated workspace route plane. */
const LegacyMilestoneGovernanceRedirect: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  if (!id) return <Navigate to="/unauthorized" replace />;
  return <LegacyWorkspaceSurfaceRedirect subPath={`milestones/${encodeURIComponent(id)}/governance`} />;
};
// SystemDashboard removed - redirects to Command Center System tab
const Tasks = lazy(() => import('./pages/Tasks'));
const NotFound = lazy(() => import('./pages/NotFound'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const Threads = lazy(() => import('./pages/Threads'));
const Milestones = lazy(() => import('./pages/Milestones'));
const MilestoneGovernanceJourneyPage = lazy(() => import('./pages/MilestoneGovernanceJourneyPage'));
const ChatHub = lazy(() => import('./pages/ChatHub'));
const MinimalChatHub = lazy(() => import('./pages/MinimalChatHub'));
const ChatHubDiagnostic = lazy(() => import('./pages/ChatHubDiagnostic'));
const SimpleChat = lazy(() => import('./pages/SimpleChat'));
const Settings = lazy(
  () => import('./pages/Settings'),
  'pages/Settings',
);
const Users = lazy(() => import('./pages/Users'));
const Invitations = lazy(() => import('./pages/Invitations'));
const Companies = lazy(() => import('./pages/Companies'));
const SecurityDashboard = lazy(() => import('./pages/SecurityDashboard'));
const AccountSecurityPage = lazy(() => import('./pages/AccountSecurityPage'));
const PlatformControlCenterPage = lazy(
  () => import('./pages/PlatformControlCenterPage'),
  'pages/PlatformControlCenterPage',
);
const PlatformTeamPage = lazy(() => import('./pages/PlatformTeamPage'));
const DueDiligenceFrameworkPage = lazy(() => import('./pages/DueDiligenceFrameworkPage'));
// Analytics now uses AnalyticsRouter for Command Center + legacy fallback
const AnalyticsRouter = lazy(
  () => import('./pages/AnalyticsRouter'),
  'pages/AnalyticsRouter',
);
const CompanyPortalGuard = lazy(() => import('./components/company/CompanyPortalGuard'));
const LegacyCompanyPortalRedirect = lazy(() => import('./components/company/LegacyCompanyPortalRedirect'));
const CompanyPortalIndexRedirect = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalIndexRedirect })),
);
const CompanyPortalCompanySelectorPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalCompanySelectorPage })),
);
const CompanyPortalDashboardPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalDashboardPage })),
);
const CompanyPortalProjectsPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalProjectsPage })),
);
const CompanyPortalProjectDetailPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalProjectDetailPage })),
);
const CompanyPortalReportsPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalReportsPage })),
);
const CompanyPortalTasksPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalTasksPage })),
);
const CompanyPortalAssetsPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalAssetsPage })),
);
const CompanyPortalDecisionsPage = lazy(() =>
  import('./pages/company/CompanyPortalPages').then((m) => ({ default: m.CompanyPortalDecisionsPage })),
);
const LegacyPartnerPortalRedirect = lazy(() => import('./components/partner/LegacyPartnerPortalRedirect'));
const LegacyVendorPortalRedirect = lazy(() => import('./components/vendor/LegacyVendorPortalRedirect'));
// Guard component for blocking unauthorized access to internal-only routes
import { useAuth } from './contexts/AuthContext';

// NOTE: Navigate already imported above; avoid re-importing to prevent duplicate declaration error
// Priority 5: Old role-based guard definitions removed (replaced with ProtectedRoute + typed capabilities)

// RequireAuth: every mode requires a resolved user session. In local preview,
// AuthContext creates a browser-local session without claiming production security.
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, mfaStatus } = useAuth();
  const location = useLocation();

  // Still resolving the local or remote session.
  if (loading) {
    return (
      <StandaloneSurface measure="reading" aria-busy="true">
        <Loader />
      </StandaloneSurface>
    );
  }

  // A partial password session never reaches Layout. AuthWrapper renders the
  // dedicated TOTP enrollment or challenge surface until Supabase reports AAL2.
  if (
    mfaStatus === 'required'
    || mfaStatus === 'enroll_required'
    || mfaStatus === 'verifying'
    || mfaStatus === 'unavailable'
  ) {
    return <AuthWrapper>{children}</AuthWrapper>;
  }

  // Not authenticated — redirect to login, preserving the requested destination.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
};

const WorkspaceBrandingRoute: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId?: string }>();
  return (
    <Navigate
      to={workspaceId
        ? `/workspaces/${encodeURIComponent(workspaceId)}/settings?tab=theme`
        : '/settings?tab=theme'}
      replace
    />
  );
};

/**
 * `/settings` is a real account surface for platform operators and a legacy
 * workspace-intent carrier for customer accounts. Keeping that distinction in
 * the route element prevents valid platform Settings links from falling into a
 * workspace selector they are not allowed to enter.
 */
const SettingsEntryRoute: React.FC = () => {
  const { user } = useAuth();
  const { platformRole } = resolveIdentityRoles(user);
  if (platformRole) return <Settings />;
  return <LegacyWorkspaceSurfaceRedirect subPath="settings" />;
};

const ProjectManagerAnalytics = lazy(() => import('./pages/ProjectManagerAnalytics'));
const AiQualityDashboard = lazy(() => import('./pages/AiQualityDashboard'));
const InvestorDashboard = lazy(() => import('./pages/InvestorDashboard'));
const InvestorInvestmentsPage = lazy(() => import('./pages/investor/InvestorInvestmentsPage'));
const InvestorMilestonesPage = lazy(() => import('./pages/investor/InvestorMilestonesPage'));
const InvestorDistributionsPage = lazy(() => import('./pages/investor/InvestorDistributionsPage'));
const InvestorDocumentsPage = lazy(() => import('./pages/investor/InvestorDocumentsPage'));
const InvestorPerformancePage = lazy(() => import('./pages/investor/InvestorPerformancePage'));
const InvestorNotificationsPage = lazy(() => import('./pages/investor/InvestorNotificationsPage'));
const InvestorDecisionsPage = lazy(() => import('./pages/investor/InvestorDecisionsPage'));
const DiagnosticPage = lazy(() => import('./pages/DiagnosticPage'));
const AuthTest = lazy(() => import('./pages/AuthTest'));
const TestDataPage = lazy(() => import('./pages/TestDataPage'));
const StorageDebugPage = lazy(() => import('./pages/StorageDebugPage'));
const Debug = lazy(() => import('./pages/Debug'));
const VerifyEmailChange = lazy(() => import('./pages/VerifyEmailChange'));
const CapitalCallback = lazy(() => import('./pages/integrations/CapitalCallback'));
const BillingPlansPage = lazy(() => import('./pages/BillingPlansPage'));
const ProjectEscalationsPage = lazy(() => import('./pages/ProjectEscalationsPage'));
const CapitalTrustPage = lazy(() => import('./components/capital/CapitalTrustPageTW'));
const InvitationsSettingsPage = lazy(() => import('./pages/InvitationsSettingsPage'));
const RolesSettingsPage = lazy(() => import('./pages/RolesSettingsPage'));
const Unauthorized = lazy(
  () => import('./pages/Unauthorized'),
  'pages/Unauthorized',
);
const E2ELogin = lazy(() => import('./pages/E2ELogin'));
const ErrorHandlingExample = lazy(() =>
  import('./components/examples/ErrorHandlingExample')
    .then(module => {
      if (module?.ErrorHandlingExample) {
        return { default: module.ErrorHandlingExample };
      }
      // Fallback: treat module as having default export
      return module as unknown as { default: React.ComponentType };
    })
);
const TabVariantShowcase = lazy(() => import('../examples/dev/TabVariantShowcase'));
const GovernanceDecisions = lazy(() => import('./pages/docs/GovernanceDecisions'));
const CapitalGatesHelp = lazy(() => import('./pages/docs/CapitalGatesHelp'));
const DataHandling = lazy(() => import('./pages/DataHandling'));
const SuspendedPage = lazy(() => import('./pages/SuspendedPage'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const PublicShareDocument = lazy(() => import('./pages/PublicShareDocument'));
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SignatureReturn = lazy(() => import('./pages/SignatureReturn'));

// -- Workspace route pages (Prompt 23 — routed workspace infrastructure) -------
const SignatureExecutionWorkspacePage = lazy(() => import('./pages/workspace/SignatureExecutionWorkspacePage'));
const DocumentSigningWorkspacePage = lazy(() => import('./pages/workspace/DocumentSigningWorkspacePage'));
const ApprovalWorkspacePage = lazy(() => import('./pages/workspace/ApprovalWorkspacePage'));
const InvestorGateReviewPage = lazy(() => import('./pages/investor/InvestorGateReviewPage'));
const GovernanceOverrideWorkspacePage = lazy(() => import('./pages/workspace/GovernanceOverrideWorkspacePage'));
const ProjectStructuringWorkspacePage = lazy(() => import('./pages/workspace/ProjectStructuringWorkspacePage'));

export const AppRoutes: React.FC = () => {
  return (
    <>
      <NavigationSyncManager />
      <Suspense fallback={
        <div role="status" aria-label="Loading view" className="flex h-full flex-col bg-app">
          <header aria-hidden="true" className="ax1-app-header flex h-16 min-h-16 items-center px-8">
            <BrandLogo variant="full" />
          </header>
          <div className="flex flex-1 items-center justify-center text-sm text-text-muted">Loading your view...</div>
        </div>
      }>
        <Routes>
          {/* Login page — outside layout, no nav bar, accessible before auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          {/* Provider callbacks must not require an AX1 role. The page is
              informational only; provider reconciliation remains authoritative. */}
          <Route path="/signatures/return" element={<SignatureReturn />} />
          {/* Authentication and account-recovery surfaces must never inherit
              workspace navigation, role controls or protected data hooks. */}
          <Route path="/unauthorized" element={
            <RequireAuth>
              <LazyLoader>
                <Unauthorized />
              </LazyLoader>
            </RequireAuth>
          } />
          <Route path="/account/security" element={
            <RequireAuth>
              <LazyLoader>
                <AccountSecurityPage />
              </LazyLoader>
            </RequireAuth>
          } />
          <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
            <Route index element={<HomeRedirect />} />
            <Route path="dashboard" element={<ErrorBoundary><DashboardSelector /></ErrorBoundary>} />
            {/* Canonical workspace-scoped surfaces. The route :workspaceId is the
                sole scope source; WorkspaceAuthorityBoundary validates membership
                before any child renders or queries. */}
            <Route path="workspaces/select" element={<LazyLoader><WorkspaceSelectionPage /></LazyLoader>} />
            <Route path="onboarding" element={<LazyLoader><OnboardingPage /></LazyLoader>} />
            <Route path="workspaces/:workspaceId" element={<WorkspaceAuthorityBoundary />}>
              {/* Index: render the existing canonical Dashboard (no replacement UI). */}
              <Route index element={<ErrorBoundary><LazyLoader><WorkspaceDashboardRouteAdapter /></LazyLoader></ErrorBoundary>} />
              <Route path="companies" element={
                <ErrorBoundary>
                  <LazyLoader>
                    <CapabilityGuard
                      check={canViewCompanies}
                      connectedCheck={(caps) => caps.has('workspace.manage')}
                      redirectTo="/unauthorized"
                    >
                      <Companies />
                    </CapabilityGuard>
                  </LazyLoader>
                </ErrorBoundary>
              } />
              <Route path="projects" element={<ErrorBoundary><LazyLoader><WorkspaceProjectsPage /></LazyLoader></ErrorBoundary>} />
              <Route path="projects/:projectId" element={<ErrorBoundary><LazyLoader><WorkspaceProjectDetailPage /></LazyLoader></ErrorBoundary>} />
              <Route path="milestones" element={<ErrorBoundary><LazyLoader><Milestones /></LazyLoader></ErrorBoundary>} />
              <Route path="milestones/:id/governance" element={<ErrorBoundary><LazyLoader><MilestoneGovernanceJourneyPage /></LazyLoader></ErrorBoundary>} />
              <Route path="calendar" element={<ErrorBoundary><LazyLoader><CalendarPage /></LazyLoader></ErrorBoundary>} />
              <Route path="tasks" element={<ErrorBoundary><LazyLoader><WorkspaceTasksPage /></LazyLoader></ErrorBoundary>} />
              <Route path="tasks/:taskId" element={<ErrorBoundary><LazyLoader><WorkspaceTaskDetailPage /></LazyLoader></ErrorBoundary>} />
              {/* Both workspace-scoped paths render the SAME existing canonical
                  Assets page via the thin adapter — no duplicate UI. */}
              <Route path="documents" element={<ErrorBoundary><LazyLoader><WorkspaceAssetsRouteAdapter /></LazyLoader></ErrorBoundary>} />
              <Route path="assets" element={<ErrorBoundary><LazyLoader><WorkspaceAssetsRouteAdapter /></LazyLoader></ErrorBoundary>} />
              <Route path="users" element={
                <LazyLoader>
                  <Users />
                </LazyLoader>
              } />
              <Route path="invitations" element={
                <LazyLoader>
                  <CapabilityGuard
                    check={canManageUsers}
                    connectedCheck={(caps) => caps.has('organization.users.invite')}
                    redirectTo="/unauthorized"
                  >
                    <Invitations />
                  </CapabilityGuard>
                </LazyLoader>
              } />
              <Route path="security" element={
                <LazyLoader>
                  <WorkspaceSecurityRouteAdapter />
                </LazyLoader>
              } />
              <Route path="settings" element={
                <LazyLoader>
                  <Settings />
                </LazyLoader>
              } />
              <Route path="settings/branding" element={<WorkspaceBrandingRoute />} />
              <Route path="settings/billing" element={
                <LazyLoader>
                  <CapabilityGuard
                    check={canViewOrganisationCommercial}
                    connectedCheck={(caps) => caps.has('organization.settings.manage')}
                    redirectTo="/unauthorized"
                  >
                    <BillingPlansPage />
                  </CapabilityGuard>
                </LazyLoader>
              } />
              <Route path="settings/invitations" element={
                <LazyLoader>
                  <CapabilityGuard
                    check={canInviteUsers}
                    connectedCheck={(caps) => caps.has('organization.users.invite')}
                    redirectTo="/unauthorized"
                  >
                    <InvitationsSettingsPage />
                  </CapabilityGuard>
                </LazyLoader>
              } />
              <Route path="settings/roles" element={
                <LazyLoader>
                  <CapabilityGuard
                    check={canManageSystemRoles}
                    connectedCheck={(caps) => caps.has('organization.users.role.change')}
                    redirectTo="/unauthorized"
                  >
                    <RolesSettingsPage />
                  </CapabilityGuard>
                </LazyLoader>
              } />
              <Route path="reports/:reportId" element={
                <LazyLoader>
                  <CapabilityGuard
                    check={(caps) => caps.includes('reports.view')}
                    connectedCheck={(caps) => caps.has('investment.reporting.read')}
                    redirectTo="/unauthorized"
                  >
                    <WorkspaceReportDetailPage />
                  </CapabilityGuard>
                </LazyLoader>
              } />
              <Route path="reports" element={
                <LazyLoader>
                  <CapabilityGuard
                    check={(caps) => caps.includes('reports.view')}
                    connectedCheck={(caps) => caps.has('investment.reporting.read')}
                    redirectTo="/unauthorized"
                  >
                    <WorkspaceReportsPage />
                  </CapabilityGuard>
                </LazyLoader>
              } />
              <Route path="chat" element={<ErrorBoundary><LazyLoader><ChatHub /></LazyLoader></ErrorBoundary>} />
              {/* Investment registry and contractual-rights management are
                  intentionally deferred from the MVP. Investor-facing capital
                  context remains available through Portfolio. */}
              <Route path="investments" element={<Navigate to=".." relative="path" replace />} />
              <Route path="decisions" element={
                <LazyLoader>
                  <WorkspaceDecisionsPage />
                </LazyLoader>
              } />
            </Route>
            {/* Legacy flat entry points: resolve accessible workspaces and route
                deterministically (0 -> no-access, 1 -> scoped route, many -> selector).
                Never auto-selects the first workspace. */}
            <Route path="projects" element={<LegacyWorkspaceSurfaceRedirect subPath="projects" />} />
            <Route path="milestones" element={<LegacyWorkspaceSurfaceRedirect subPath="milestones" />} />
            <Route path="calendar" element={<LegacyWorkspaceSurfaceRedirect subPath="calendar" />} />
            <Route path="chat" element={<LegacyWorkspaceSurfaceRedirect subPath="chat" />} />
            <Route path="tasks" element={<LegacyWorkspaceSurfaceRedirect subPath="tasks" />} />
            <Route path="reports" element={<LegacyWorkspaceSurfaceRedirect subPath="reports" />} />
            <Route path="documents" element={<LegacyWorkspaceSurfaceRedirect subPath="assets" />} />
            <Route path="assets" element={<LegacyWorkspaceSurfaceRedirect subPath="assets" />} />
            <Route path="investments" element={<Navigate to="/" replace />} />
            <Route path="decisions" element={<LegacyWorkspaceSurfaceRedirect subPath="decisions" />} />

            {/* Lazily loaded routes wrapped in Suspense for better loading experience */}
            <Route path="projects/:projectId" element={
              <ErrorBoundary>
                <LegacyProjectDetailRedirect />
              </ErrorBoundary>
            } />
            <Route path="tasks/:taskId" element={
              <LazyLoader>
                <ErrorBoundary>
                  <Tasks />
                </ErrorBoundary>
              </LazyLoader>
            } />
            <Route path="messages" element={
              <LazyLoader>
                <Threads />
              </LazyLoader>
            } />
            <Route path="milestones/:id/governance" element={<LegacyMilestoneGovernanceRedirect />} />
            <Route path="capital" element={
              <CapabilityGuard
                check={(caps) => caps.includes('projects.view') || caps.includes('gates.view')}
                redirectTo="/unauthorized"
              >
                <LazyLoader>
                  <Milestones />
                </LazyLoader>
              </CapabilityGuard>
            } />
            <Route path="capital/trust" element={
              <AccountCapabilityGuard requiredAny={['investments.rights.view']}>
                <LazyLoader>
                  <CapitalTrustPage />
                </LazyLoader>
              </AccountCapabilityGuard>
            } />
            {ENABLE_INTERNAL_ROUTES && <Route path="chat-diagnostic" element={
              <LazyLoader>
                <ChatHubDiagnostic />
              </LazyLoader>
            } />}
            {ENABLE_INTERNAL_ROUTES && <Route path="simple-chat" element={
              <LazyLoader>
                <SimpleChat />
              </LazyLoader>
            } />}
            {ENABLE_INTERNAL_ROUTES && <Route path="test" element={<ErrorBoundary><TestPage /></ErrorBoundary>} />}
            {ENABLE_INTERNAL_ROUTES && <Route path="debug-progress" element={<ErrorBoundary><DebugProgressTest /></ErrorBoundary>} />}
            {ENABLE_INTERNAL_ROUTES && <Route path="progress-test" element={<ErrorBoundary><ProgressBarTest /></ErrorBoundary>} />}
            {ENABLE_INTERNAL_ROUTES && <Route path="design-system" element={<ErrorBoundary><DesignTokensDemo /></ErrorBoundary>} />}
            {ENABLE_INTERNAL_ROUTES && <Route path="diagnostic" element={
              <LazyLoader>
                <DiagnosticPage />
              </LazyLoader>
            } />}
            {ENABLE_INTERNAL_ROUTES && <Route path="auth-test" element={
              <LazyLoader>
                <AuthTest />
              </LazyLoader>
            } />}
            {ENABLE_INTERNAL_ROUTES && <Route path="test-data" element={
              <LazyLoader>
                <TestDataPage />
              </LazyLoader>
            } />}
            <Route path="settings" element={<SettingsEntryRoute />} />
            <Route path="settings/branding" element={<WorkspaceBrandingRoute />} />
            {/* Capital Integration OAuth-like callback */}
            <Route path="settings/integrations/capital/callback" element={
              <LazyLoader>
                <CapitalCallback />
              </LazyLoader>
            } />
            {/* Billing & Plans - admin only */}
            <Route path="settings/billing" element={
              <LazyLoader>
                <CapabilityGuard check={canViewOrganisationCommercial} redirectTo="/unauthorized">
                  <BillingPlansPage />
                </CapabilityGuard>
              </LazyLoader>
            } />
            {/* Legacy invitation settings entry resolves an explicit workspace. */}
            <Route path="settings/invitations" element={
              <LegacyWorkspaceSurfaceRedirect
                subPath="settings/invitations"
                connectedServerResolution
              />
            } />
            {/* Membership management settings - admin and ops */}
            <Route path="settings/roles" element={
              <LazyLoader>
                <CapabilityGuard check={canManageSystemRoles} redirectTo="/unauthorized">
                  <RolesSettingsPage />
                </CapabilityGuard>
              </LazyLoader>
            } />
            <Route path="users" element={<LegacyWorkspaceSurfaceRedirect subPath="users" />} />
            <Route path="invitations" element={
              <LegacyWorkspaceSurfaceRedirect subPath="invitations" connectedServerResolution />
            } />
            <Route path="companies" element={<LegacyWorkspaceSurfaceRedirect subPath="companies" />} />
            <Route path="security" element={<LegacyWorkspaceSurfaceRedirect subPath="security" />} />
            <Route path="platform/security" element={
              <LazyLoader>
                <SecurityDashboard scopeWorkspaceId={null} />
              </LazyLoader>
            } />
            <Route path="platform" element={
              <LazyLoader>
                <PlatformControlCenterPage section="overview" />
              </LazyLoader>
            } />
            <Route path="platform/customers" element={
              <LazyLoader>
                <PlatformControlCenterPage section="customers" />
              </LazyLoader>
            } />
            <Route path="platform/commercial" element={
              <LazyLoader>
                <PlatformControlCenterPage section="commercial" />
              </LazyLoader>
            } />
            <Route path="platform/operations" element={
              <LazyLoader>
                <PlatformControlCenterPage section="operations" />
              </LazyLoader>
            } />
            <Route path="platform/access" element={
              <LazyLoader>
                <PlatformControlCenterPage section="access" />
              </LazyLoader>
            } />
            <Route path="platform/team" element={
              <LazyLoader>
                <PlatformTeamPage />
              </LazyLoader>
            } />
            <Route path="dd-framework" element={
              <LazyLoader>
                <DueDiligenceFrameworkPage />
              </LazyLoader>
            } />
            <Route path="security/compliance" element={<Navigate to="/security?tab=compliance" replace />} />
            <Route path="security/trust" element={<Navigate to="/security?tab=compliance" replace />} />
            <Route path="security/rbac-admin" element={<Navigate to="/settings/roles" replace />} />
            <Route path="rbac-admin" element={<Navigate to="/settings/roles" replace />} />
            <Route path="execution/escalations" element={
              <LazyLoader>
                <ProjectEscalationsPage />
              </LazyLoader>
            } />
            {/* /system redirects to Command Center System tab */}
            <Route path="system" element={<Navigate to="/analytics?tab=system" replace />} />
            {ENABLE_INTERNAL_ROUTES && <Route path="debug" element={
              <LazyLoader>
                <Debug />
              </LazyLoader>
            } />}
            {ENABLE_INTERNAL_ROUTES && <Route path="tab-showcase" element={
              <LazyLoader>
                <TabVariantShowcase />
              </LazyLoader>
            } />}
            <Route path="analytics" element={
              <LazyLoader>
                <AnalyticsRouter />
              </LazyLoader>
            } />
            <Route path="incident-reports" element={<Navigate to="/security?tab=incidents" replace />} />
            <Route path="verify-email-change" element={
              <LazyLoader>
                <VerifyEmailChange />
              </LazyLoader>
            } />
            {ENABLE_E2E_ROUTES && (
              <Route path="e2e-login" element={
                <LazyLoader>
                  <E2ELogin />
                </LazyLoader>
              } />
            )}
            {ENABLE_INTERNAL_ROUTES && <Route path="error-handling-examples" element={
              <LazyLoader>
                <ErrorHandlingExample />
              </LazyLoader>
            } />}
            {/* Documentation routes */}
            <Route path="docs/governance-decisions" element={
              <LazyLoader>
                <GovernanceDecisions />
              </LazyLoader>
            } />
            <Route path="docs/capital-gates" element={
              <LazyLoader>
                <CapitalGatesHelp />
              </LazyLoader>
            } />
            {/* Data Processing & Handling (public) */}
            <Route path="data-handling" element={
              <LazyLoader>
                <DataHandling />
              </LazyLoader>
            } />
            <Route path="analytics/project-manager" element={
              <LazyLoader withErrorBoundary={false}>
                <ProtectedRoute capability="analytics.view.projects">
                  <ProjectManagerAnalytics />
                </ProtectedRoute>
              </LazyLoader>
            } />
            {/* ─── Company Portal (Gate R2 Phase D2) ───
                Company Representative accessing the Company Portal.
                Canonical: /companies/:companyId/* — allowlisted surfaces only.
                Legacy /client/* and /portal/client redirect via entry resolver. */}
            <Route path="companies/select" element={
              <LazyLoader>
                <CompanyPortalCompanySelectorPage />
              </LazyLoader>
            } />
            <Route path="companies/:companyId" element={
              <LazyLoader>
                <CompanyPortalGuard />
              </LazyLoader>
            }>
              <Route index element={
                <LazyLoader>
                  <CompanyPortalIndexRedirect />
                </LazyLoader>
              } />
              <Route path="dashboard" element={
                <LazyLoader>
                  <CompanyPortalDashboardPage />
                </LazyLoader>
              } />
              <Route path="projects" element={
                <LazyLoader>
                  <CompanyPortalProjectsPage />
                </LazyLoader>
              } />
              <Route path="projects/:projectId" element={
                <LazyLoader>
                  <CompanyPortalProjectDetailPage />
                </LazyLoader>
              } />
              <Route path="reports" element={
                <LazyLoader>
                  <CompanyPortalReportsPage />
                </LazyLoader>
              } />
              <Route path="tasks" element={
                <LazyLoader>
                  <CompanyPortalTasksPage />
                </LazyLoader>
              } />
              <Route path="assets" element={
                <LazyLoader>
                  <CompanyPortalAssetsPage />
                </LazyLoader>
              } />
              <Route path="decisions" element={
                <LazyLoader>
                  <CompanyPortalDecisionsPage />
                </LazyLoader>
              } />
              <Route path="participants" element={
                <Navigate to="../projects" relative="path" replace />
              } />
              <Route path="activity" element={
                <Navigate to="../dashboard" relative="path" replace />
              } />
            </Route>

            {/* Legacy Client Portal aliases → Company Portal */}
            <Route path="client/dashboard" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/projects" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/projects/:projectId" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/reports" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/reports/*" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/tasks" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/assets" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/decisions" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/participants" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/activity" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="client/*" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />
            <Route path="portal/client" element={
              <LazyLoader><LegacyCompanyPortalRedirect /></LazyLoader>
            } />

            {ENABLE_INTERNAL_ROUTES && <Route path="ai-quality" element={
              <LazyLoader>
                <AiQualityDashboard />
              </LazyLoader>
            } />}
            {/* Retired relationship portal routes are compatibility redirects only.
                A scoped company relationship lands in its authorized workspace, never in a
                separate product shell or generic operating surface. */}
            <Route path="partners/*" element={<LazyLoader><LegacyPartnerPortalRedirect /></LazyLoader>} />
            <Route path="vendors/*" element={<LazyLoader><LegacyVendorPortalRedirect /></LazyLoader>} />
            <Route path="portal/partner/*" element={<LazyLoader><LegacyPartnerPortalRedirect /></LazyLoader>} />
            <Route path="portal/vendor/*" element={<LazyLoader><LegacyVendorPortalRedirect /></LazyLoader>} />
            {ENABLE_INTERNAL_ROUTES && <Route path="toast-test" element={<ErrorBoundary><ToastTest /></ErrorBoundary>} />}
            {ENABLE_INTERNAL_ROUTES && <Route path="storage-debug" element={
              <LazyLoader>
                <StorageDebugPage />
              </LazyLoader>
            } />}
            <Route path="404" element={
              <LazyLoader>
                <NotFound />
              </LazyLoader>
            } />
            {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                WORKSPACE ROUTES — Routed execution pages (Prompt 23)
                Capital, Approval, and Governance workspaces render as
                first-class routes within the main Layout shell.
                Props are passed via location.state from launchers.
                â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
            <Route path="workspace" element={<WorkspacePageLayout />}>
              <Route path="capital/release/:gateId" element={
                <Navigate to="/capital" replace />
              } />
              <Route path="document-sign/:documentId" element={
                <LazyLoader>
                  <DocumentSigningWorkspacePage />
                </LazyLoader>
              } />
              <Route path="approve/:id" element={
                <LazyLoader>
                  <ApprovalWorkspacePage />
                </LazyLoader>
              } />
              <Route path="governance/:id" element={
                <LazyLoader>
                  <GovernanceOverrideWorkspacePage />
                </LazyLoader>
              } />
              {/* Retired finance-execution deep links fail closed to the
                  governed Capital overview. These surfaces are outside the
                  AX1 v1 production boundary. */}
              <Route path="payout/*" element={<Navigate to="/capital" replace />} />
              <Route path="settlement/*" element={<Navigate to="/capital" replace />} />
              <Route path="user/:id/permissions" element={<Navigate to="/settings/roles" replace />} />
              <Route path="vault/*" element={<Navigate to="/capital" replace />} />
              <Route path="disbursement/*" element={<Navigate to="/capital" replace />} />
              <Route path="beneficiary/*" element={<Navigate to="/capital" replace />} />
              <Route path="projects/structure/:draftId" element={
                <LazyLoader>
                  <ProjectStructuringWorkspacePage />
                </LazyLoader>
              } />
              <Route path="investments/:investmentId/rights" element={<Navigate to="/" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Route>
          {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
              INVESTOR PORTAL — Isolated shell with investor-only nav
              All /investor/* routes render inside InvestorAppShell
              â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
          <Route path="/investor" element={<InvestorAppShell />}>
            <Route index element={<InvestorLandingRedirect />} />
            <Route path="dashboard" element={
              <InvestorCapabilityGuard requiredAny={['investor.dashboard.view']}>
                <LazyLoader>
                  <InvestorDashboard />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="investments" element={
              <InvestorCapabilityGuard requiredAny={['investor.investments.view']}>
                <LazyLoader>
                  <InvestorInvestmentsPage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="milestones" element={
              <InvestorCapabilityGuard requiredAny={['investor.milestones.view']}>
                <LazyLoader>
                  <InvestorMilestonesPage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="tasks" element={<Navigate to="/investor/decisions" replace />} />
            <Route path="distributions" element={
              <InvestorCapabilityGuard requiredAny={['investor.distributions.view']}>
                <LazyLoader>
                  <InvestorDistributionsPage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="data-rooms" element={
              <InvestorCapabilityGuard requiredAny={['investor.documents.view', 'investor.reports.view']}>
                <LazyLoader>
                  <InvestorDocumentsPage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="documents" element={<Navigate to="/investor/data-rooms" replace />} />
            <Route path="reports" element={
              <InvestorCapabilityGuard requiredAny={['investor.reports.view']}>
                <Navigate to="/investor/data-rooms?section=reports" replace />
              </InvestorCapabilityGuard>
            } />
            <Route path="performance" element={
              <InvestorCapabilityGuard requiredAny={['investor.investments.view']}>
                <LazyLoader>
                  <InvestorPerformancePage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="calendar" element={
              <InvestorCapabilityGuard requiredAny={['investor.calendar.view']}>
                <LazyLoader>
                  <CalendarPage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="chat" element={
              <InvestorCapabilityGuard requiredAny={['investor.chat.access']}>
                <LazyLoader>
                  <ChatHub />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="notifications" element={
              <InvestorCapabilityGuard requiredAny={['investor.notifications.view']}>
                <LazyLoader>
                  <InvestorNotificationsPage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="decisions" element={
              <InvestorCapabilityGuard requiredAny={['gates.view']}>
                <LazyLoader>
                  <InvestorDecisionsPage />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="security" element={<Navigate to="/investor/settings?tab=trust" replace />} />
            <Route path="help" element={<Navigate to="/investor/settings?tab=faq" replace />} />
            <Route path="settings" element={
              <InvestorCapabilityGuard requiredAny={['investor.settings.manage']}>
                <LazyLoader>
                  <Settings />
                </LazyLoader>
              </InvestorCapabilityGuard>
            } />
            {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
                INVESTOR WORKSPACE ROUTES — Signature execution
                Rendered inside InvestorAppShell so investor nav and
                actions context are preserved.
                Props are passed via location.state from launchers.
                â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
            <Route path="workspace/gate-review/:decisionId" element={
              <InvestorCapabilityGuard requiredAny={['gates.view']}>
                <LazyLoader><InvestorGateReviewPage /></LazyLoader>
              </InvestorCapabilityGuard>
            } />
            <Route path="workspace" element={
              <InvestorCapabilityGuard requiredAny={['gates.view']}>
                <WorkspacePageLayout />
              </InvestorCapabilityGuard>
            }>
              <Route path="signature/:signatureId" element={
                <LazyLoader>
                  <SignatureExecutionWorkspacePage />
                </LazyLoader>
              } />
              <Route path="capital-approval/*" element={<Navigate to="/investor/decisions" replace />} />
            </Route>
          </Route>
          {/* Routes outside of Layout to test rendering */}
          {ENABLE_INTERNAL_ROUTES && <Route path="/test-standalone" element={<TestPage />} />}
          {ENABLE_INTERNAL_ROUTES && <Route path="/minimal-chat" element={
            <LazyLoader withErrorBoundary={false}>
              <MinimalChatHub />
            </LazyLoader>
          } />}
          {/* Suspended account page - outside layout, no nav */}
          <Route path="/suspended" element={
            <LazyLoader>
              <SuspendedPage />
            </LazyLoader>
          } />
          {/* Accept invitation page - standalone, works before/after login */}
          <Route path="/accept-invite" element={
            <LazyLoader>
              <AcceptInvite />
            </LazyLoader>
          } />
          <Route path="/share/:token" element={
            <LazyLoader>
              <PublicShareDocument />
            </LazyLoader>
          } />
        </Routes>
      </Suspense>
    </>
  );
};

import { MilestoneEditorProvider } from './contexts/MilestoneEditorContext';
import { OverlayOrchestratorProvider } from './lib/navigation/OverlayOrchestratorContext';

const App: React.FC = () => {
  if (ENVIRONMENT_CONFIGURATION_ERROR) {
    return (
      <StandaloneSurface measure="reading">
        <section role="alert" className="w-full rounded-lg border border-border-subtle bg-surface-card p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold">Environment configuration conflict</h1>
          <p className="text-sm text-text-muted">{ENVIRONMENT_CONFIGURATION_ERROR}</p>
        </section>
      </StandaloneSurface>
    );
  }

  const connectedEnvironmentValidation = IS_DEMO_MODE ? null : validateEnvironment();

  if (connectedEnvironmentValidation && !connectedEnvironmentValidation.valid) {
    return (
      <StandaloneSurface measure="reading">
        <section role="alert" className="w-full rounded-lg border border-border-subtle bg-surface-card p-6 text-center shadow-sm">
          <h1 className="text-lg font-semibold">Connected environment not ready</h1>
          <p className="text-sm text-text-muted">This environment cannot fall back to Demo data.</p>
          <ul className="space-y-1 text-left text-sm text-text-muted">
            {connectedEnvironmentValidation.errors.map((error) => <li key={error}>• {error}</li>)}
          </ul>
        </section>
      </StandaloneSurface>
    );
  }

  return (
    <Router>
      <OverlayOrchestratorProvider>
        <MilestoneEditorProvider>
          <AppRoutes />
        </MilestoneEditorProvider>
      </OverlayOrchestratorProvider>
    </Router>
  );
};

export default App;

